import { Button, Modal, Tabs } from '@mantine/core'
import '@mantine/core/styles.css'
import { useDisclosure } from '@mantine/hooks'
import { IconLock, IconMail } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useLogin } from '../../api/hooks/useAuth'
import { useAuthStore } from '../../store/useAuthStore'
import styles from '../../styles/admin/SignInPage.module.scss'
import BaseButton from '../common/BaseButton'
import QuizCheckbox from '../common/QuizCheckBox'
import SignInInput from '../common/SignInInput'
import { Icon } from '../../assets/icons'
import { useNavigate } from 'react-router-dom'
import BaseInput from '../common/BaseInput'
import { formatPhoneNumber } from '../../utils/utils'
import { apiRequest } from '../../utils/api'

const SignInContent = () => {
  const navigate = useNavigate()
  const { keepLoggedIn } = useAuthStore()
  const [opened, { open, close }] = useDisclosure(false)
  const [modalContent, setModalContent] = useState('')
  const [userId, setUserId] = useState('')
  const [password, setPassword] = useState('')
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(keepLoggedIn)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('id')
  const [phone, setPhone] = useState('')
  const [isVerified, setIsVerified] = useState(false)
  const [findIdName, setFindIdName] = useState('')
  const [findIdPhone, setFindIdPhone] = useState('')
  const [foundUserId, setFoundUserId] = useState('')
  const [findPwId, setFindPwId] = useState('')
  const [findPwName, setFindPwName] = useState('')
  const [findPwPhone, setFindPwPhone] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const loginMutation = useLogin()


  useEffect(() => {
    if (loginMutation.isSuccess) {
      if (loginMutation.data.success === 'Y') {
        setModalContent('Login successful. Redirecting...')
        open()
      } else if (loginMutation.data.success === 'N') {
        setModalContent('Login failed. Please check your ID and password.')
        open()
      }
    } else if (loginMutation.isError) {
      setModalContent('Network error. Please try again.')
      open()
    }
  }, [loginMutation.isSuccess, loginMutation.isError, loginMutation.data, open])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!userId.trim()) {
      setModalContent('Please enter your ID.')
      open()
      return
    }

    if (!password.trim()) {
      setModalContent('Please enter your password.')
      open()
      return
    }

    if (userId.length < 3) {
      setModalContent('ID must be at least 3 characters.')
      open()
      return
    }

    if (password.length < 3) {
      setModalContent('Password must be at least 3 characters.')
      open()
      return
    }

    loginMutation.mutate({
      USER_ID: userId.trim(),
      PASSWORD: password.trim(),
      STATE: 'Y',
      KEEP_LOGGED_IN: keepMeLoggedIn,
    })
  }

  const handleFindId = async () => {
    if (!findIdName.trim() || !findIdPhone.trim()) {
      setModalContent('Name and phone number are required.')
      open()
      return
    }
    const cleanPhone = findIdPhone.replace(/-/g, '')

    try {
      const res = await apiRequest('/find-id', 'POST', {
        NAME: findIdName,
        PHONE: cleanPhone,
      })

      if (res.data) {
        setFoundUserId(res.data)
      } else {
        setModalContent('No matching account found.')
        open()
      }
    } catch (err) {
      setModalContent('A server error occurred.')
      open()
    }
  }

  const handleVerifyClick = async () => {
    if (!findPwId.trim() || !findPwName.trim() || !findPwPhone.trim()) {
      setModalContent('ID, name, and phone number are required.')
      open()
      return
    }

    try {
      const res = await apiRequest('/verify-user', 'POST', {
        USER_ID: findPwId,
        NAME: findPwName,
        PHONE: findPwPhone.replace(/-/g, ''),
      })

      if (res.success) {
        setIsVerified(true)
        setModalContent('Verification successful. Please enter a new password.')
        open()
      } else {
        setModalContent('No matching user information found.')
        open()
      }
    } catch (err) {
      setModalContent('A server error occurred.')
      open()
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setModalContent('Please enter the new password.')
      open()
      return
    }

    if (newPassword !== confirmPassword) {
      setModalContent('Passwords do not match.')
      open()
      return
    }

    try {
      const res = await apiRequest('/reset-password', 'POST', {
        USER_ID: findPwId,
        PASSWORD: newPassword,
      })

      if (res.success) {
        setModalContent('Password has been successfully changed.')
        open()
        setIsVerified(false)
        setFindPwId('')
        setFindPwName('')
        setFindPwPhone('')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        setModalContent('Password change failed.')
        open()
      }
    } catch (err) {
      setModalContent('A server error occurred.')
      open()
    }
  }

  const handleNavigateToLanding = () => {
    navigate('/landing')
  }

  const handlePasswordModalOpen = () => {
    setIsPasswordModalOpen(true)
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhone(formatted)
  }

  return (
    <div className={styles.formFields}>
      <form onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <p className={styles.fieldLabel}>ID</p>
          <SignInInput
            icon={<Icon name="email" color="#0D2A4F" size={18} />}
            placeholder="Enter your ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            name="userId"
            autoComplete="username"
          />
        </div>

        <div className={styles.inputGroup}>
          <p className={styles.fieldLabel}>Password</p>
          <SignInInput
            type="password"
            icon={<Icon name="lockOpen" color="#0D2A4F" size={20} />}
            isHide={true}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            name="password"
            autoComplete="current-password"
          />
        </div>

        <div className={styles.checkboxContainer}>
          <div className={styles.checkboxWrapper}>
            <QuizCheckbox
              checked={keepMeLoggedIn}
              onChange={setKeepMeLoggedIn}
            />
          </div>
          <p className={styles.smallTextWrapper}>Remember ID</p>
          <motion.p
            className={styles.passwordTextWrapper}
            onClick={handlePasswordModalOpen}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            Forgot password?
          </motion.p>
        </div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
        >
          <BaseButton
            type="submit"
            className={styles.signInButton}
            style={{ background: '#162543', borderColor: '#162543' }}
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? 'Signing in...' : 'Sign in'}
          </BaseButton>
        </motion.div>

        <div className={styles.orDivider}>
          <div className={styles.dividerLine} />
          <span className={styles.orText}>or</span>
          <div className={styles.dividerLine} />
        </div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
        >
          <BaseButton
            type="button"
            className={styles.getStartedButton}
            onClick={handleNavigateToLanding}
            style={{
              background: '#ff7a2f',
              borderColor: '#ff7a2f',
              boxShadow: '0 6px 16px rgba(255, 122, 47, 0.22)',
            }}
          >
            Get Started →
          </BaseButton>
        </motion.div>
      </form>

        {/* 아이디 비밀번호 찾기 모달 */}
        <Modal
          opened={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          withCloseButton={true}
          centered
          size={440}
          value={activeTab}
          radius="lg"
          styles={{
            header: {
              position: 'absolute',
              top: 0,
              right: 0,
              background: 'transparent',
              padding: '12px 12px 0 0',
              minHeight: 'unset',
              zIndex: 10,
            },
            body: { padding: '32px' },
            content: { overflow: 'hidden', position: 'relative' },
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {/* 아이콘 + 타이틀 */}
            <div className={styles.findModalTop}>
              <div className={styles.findModalIconCircle}>🔑</div>
              <p className={styles.findModalTitle}>Account Recovery</p>
              <p className={styles.findModalSubtitle}>Find your ID or reset your password.</p>
            </div>

            <Tabs
              color="#425AA8"
              value={activeTab}
              onChange={setActiveTab}
            >
              <Tabs.List className={styles.findTabsList}>
                <Tabs.Tab className={styles.findTab} value="id">Find ID</Tabs.Tab>
                <Tabs.Tab className={styles.findTab} value="password">Find Password</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="id">
                <div className={styles.findPanelBody}>
                  <BaseInput
                    placeholder="Name"
                    autoComplete="off"
                    className={styles.findInput}
                    value={findIdName}
                    onChange={(e) => setFindIdName(e.target.value)}
                  />
                  <BaseInput
                    placeholder="Phone Number"
                    name="phone"
                    value={findIdPhone}
                    className={styles.findInput}
                    onChange={(e) => setFindIdPhone(formatPhoneNumber(e.target.value))}
                  />
                  {foundUserId && (
                    <motion.div
                      className={styles.foundUserIdBoxHighlight}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>Your ID is <strong>{foundUserId}</strong></p>
                    </motion.div>
                  )}
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <BaseButton className={styles.findConfirmBtn} onClick={handleFindId}>
                      Confirm
                    </BaseButton>
                  </motion.div>
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="password">
                <div className={styles.findPanelBody}>
                  <BaseInput
                    placeholder="ID"
                    autoComplete="off"
                    className={styles.findInput}
                    value={findPwId}
                    onChange={(e) => setFindPwId(e.target.value)}
                  />
                  <BaseInput
                    placeholder="Name"
                    autoComplete="off"
                    className={styles.findInput}
                    value={findPwName}
                    onChange={(e) => setFindPwName(e.target.value)}
                  />
                  <BaseInput
                    placeholder="Phone Number"
                    name="phone"
                    value={findPwPhone}
                    onChange={(e) => setFindPwPhone(formatPhoneNumber(e.target.value))}
                    className={styles.findInput}
                  />
                  {isVerified && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className={styles.verifiedSection}
                    >
                      <div className={styles.verifiedBadge}>✓ Identity verified</div>
                      <BaseInput
                        placeholder="New password"
                        name="password"
                        className={styles.findInput}
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <BaseInput
                        placeholder="Confirm new password"
                        name="confirmPassword"
                        className={styles.findInput}
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </motion.div>
                  )}
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <BaseButton
                      className={styles.findConfirmBtn}
                      onClick={isVerified ? handleResetPassword : handleVerifyClick}
                    >
                      {isVerified ? 'Reset Password' : 'Confirm'}
                    </BaseButton>
                  </motion.div>
                </div>
              </Tabs.Panel>
            </Tabs>
          </motion.div>
        </Modal>

        <Modal
          opened={opened}
          onClose={close}
          withCloseButton={true}
          centered
          size="lg"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={styles.modalContentWrapper}
          >
            <img
              src="/assets/smart-green-monster.png"
              alt="image"
              className={styles.modalImage}
            />
            {modalContent}
          </motion.div>
        </Modal>
    </div>
  )
}

export default SignInContent
